import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, BriefcaseIcon, BuildingOffice2Icon, CheckCircleIcon, EnvelopeIcon, IdentificationIcon, MapPinIcon, Squares2X2Icon, UserIcon } from "@heroicons/react/24/outline";
import Input from "components/Input/Input";
import SectionHero from "components/SectionHero/SectionHero";
import rightImg from "images/finvue-homepage-hero-section-right.png";
import React, { useState } from "react";

interface ChecklistDocument {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
  downloadUrl?: string;
  usesCompaniesHouse?: boolean;
}

const COMPANIES_HOUSE_BASE = "https://find-and-update.company-information.service.gov.uk/company/";

const documents: ChecklistDocument[] = [
  {
    id: 1,
    title: "Company Documents",
    description: "Download Incorporation PDF from Companies House.",
    icon: BuildingOffice2Icon,
    usesCompaniesHouse: true,
  },
  {
    id: 2,
    title: "Proof of Address",
    description: "Required for Company. Must be dated within the last 3 months.",
    icon: MapPinIcon,
  },
  {
    id: 3,
    title: "Proof of Address",
    description: "Required for Director/UBO. Must be dated within the last 3 months.",
    icon: MapPinIcon,
  },
  {
    id: 4,
    title: "Photo ID Verification",
    description: "Required for Directors/UBO and ID verification and for Liveness Check.",
    icon: IdentificationIcon,
  },
  {
    id: 5,
    title: "Master Service Agreement (Payroll Applicants)",
    description: "Active Agency agreements.",
    icon: BriefcaseIcon,
  },
  {
    id: 6,
    title: "Directors CV (Payroll Applicants)",
    description: "Payroll compliant CV",
    icon: UserIcon,
    downloadUrl: `${process.env.PUBLIC_URL ?? ""}/docs/CVTemplate.docx`,
  },
  {
    id: 7,
    title: "Flow of Funds (Payroll Applicants)",
    description: "Download our template, complete with your flow of funds information.",
    icon: ArrowPathIcon,
    downloadUrl: `${process.env.PUBLIC_URL ?? ""}/docs/Flow-of-Funds-template.pdf`,
  },
  {
    id: 8,
    title: "Organisation Structure Chart",
    description: "Download our template, complete with your company structure.",
    icon: Squares2X2Icon,
    downloadUrl: `${process.env.PUBLIC_URL ?? ""}/docs/Template-Organisational-Structure-Payroll-Company.pdf`,
  },
];

const CompaniesHouseInput: React.FC = () => {
  const [companyNumber, setCompanyNumber] = useState("");

  const openCompaniesHouse = () => {
    const trimmed = companyNumber.trim();
    if (!trimmed) return;
    window.open(`${COMPANIES_HOUSE_BASE}${trimmed}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-4">
      <Input placeholder="Enter company number" value={companyNumber} onChange={(event) => setCompanyNumber(event.target.value)} />
      <button
        type="button"
        onClick={openCompaniesHouse}
        disabled={!companyNumber.trim()}
        className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
        Open Companies House
      </button>
    </div>
  );
};

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M20.52 3.48A11.84 11.84 0 0 0 12.03 0C5.39 0 0 5.39 0 12.03c0 2.12.55 4.19 1.6 6.02L0 24l6.11-1.6a12 12 0 0 0 5.92 1.51h.01c6.64 0 12.03-5.39 12.03-12.03 0-3.21-1.25-6.22-3.55-8.4Zm-8.49 18.4h-.01a9.95 9.95 0 0 1-5.05-1.38l-.36-.21-3.63.95.97-3.54-.23-.36a9.95 9.95 0 0 1-1.53-5.31C2.19 6.49 6.49 2.2 12.03 2.2c2.65 0 5.15 1.03 7.03 2.91a9.85 9.85 0 0 1 2.91 7.02c0 5.54-4.3 9.75-9.94 9.75Zm5.45-7.45c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.16-.17.2-.35.22-.65.08-.3-.15-1.27-.47-2.42-1.49a9.03 9.03 0 0 1-1.67-2.08c-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.08-.15-.67-1.62-.92-2.21-.24-.58-.48-.5-.67-.51h-.57c-.2 0-.52.08-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.1 4.48.71.31 1.27.5 1.71.64.72.23 1.37.2 1.89.12.58-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.08-.13-.27-.2-.57-.35Z" />
  </svg>
);

const RequireDocs: React.FC = () => {
  return (
    <section className="overflow-hidden">
      <div className="bg-custom-gradient rounded-b-3xl">
        <div className="container">
          <SectionHero
            rightImg={rightImg}
            className="pt-6 text-white"
            topHeading="Pre-Application Checklist"
            heading={
              <div className="text-3xl lg:text-6xl text-slate-50 font-extrabold">
                Get ready for your
                <span className="!text-blue-500 block">Application</span>
              </div>
            }
            // btnTextPrimary="Upload Now"
            // btnPrimaryUrl="/upload"
            // btnTextSecondary="View Templates"
            // btnSecondaryUrl="#templates"
            subHeading="Incomplete or missing documentation can delay your application and may present compliance and regulatory risks. Please ensure all required documents are prepared accurately before submitting."
            subHeadingColor="text-slate-200"
          />
        </div>
      </div>

      <section id="templates" className="container py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-slate-900 md:text-4xl">Required Documents</h2>
          <p className="mt-4 text-slate-600">Prepare all of the following documents before submitting your application.</p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {documents.map((doc) => {
            const Icon = doc.icon;
            return (
              <article key={doc.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                  <Icon className="h-6 w-6 text-blue-700" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{doc.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{doc.description}</p>

                {doc.usesCompaniesHouse && <CompaniesHouseInput />}

                {doc.downloadUrl && (
                  <a
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex w-full items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-700 hover:text-blue-700"
                  >
                    <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                    Download Template
                  </a>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center text-center text-sm text-slate-600">
          <CheckCircleIcon className="mb-2 h-5 w-5 text-blue-700" />
          <p>All documents must be valid and up to date at the time of submission.</p>
          <p>Once you are ready to start your application, please contact us using the option below</p>
        </div>

        <div className="mx-auto mt-12 grid max-w-2xl gap-6 sm:grid-cols-2">
          <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-[#25D366]">
              <WhatsAppIcon className="h-5 w-5" />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">Contact us via WhatsApp</p>
            <a
              href="https://api.whatsapp.com/send?phone=4403300433536"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center rounded-full bg-[#25D366] px-6 py-2.5 text-sm font-semibold text-white transition hover:brightness-95"
            >
              Contact
            </a>
          </div>

          <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
              <EnvelopeIcon className="h-5 w-5" />
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">Contact us</p>
            <a
              href="https://www.finvue.co.uk/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center rounded-full border border-blue-700 px-6 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-700 hover:text-white"
            >
              Contact
            </a>
          </div>
        </div>
      </section>
    </section>
  );
};

export default RequireDocs;
